import { useState, type ReactElement, type ReactNode } from 'react';
import { CircleAlert } from 'lucide-react';
import type { ConfirmarEntregaAbastecimentoInput } from '@pieltec/contracts';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { LocalizacaoSelect } from '../../components/estoque/localizacao-select';
import { FormMessage } from '../../components/ui/form-message';
import { useToast } from '../../components/ui/toast';
import { abastecimentoApi, ErroAbastecimento } from '../../lib/estoque/abastecimento-api';
import type { LinhaFila } from './abastecimento-linha';

export const TITULO_ENTREGA = 'Confirmar entrega física?';
export const CORPO_ENTREGA = 'A quantidade informada será registrada no destino e o saldo pendente desta solicitação será atualizado.';
export const TEXTO_CONFLITO = 'Este registro foi alterado por outra pessoa. Recarregue para conferir a situação atual.';
export const TEXTO_INCERTO = 'Não foi possível confirmar o resultado desta ação. Consulte a situação atual antes de tentar novamente.';
export const TEXTO_OFFLINE = 'Conexão necessária para confirmar esta ação.';

const BOTAO = 'inline-flex min-h-11 items-center justify-center rounded-md border border-border px-4 font-semibold disabled:opacity-50';
const BOTAO_PRIMARIO = 'inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50';
const CAMPO = 'mt-1 min-h-11 w-full rounded-md border border-border bg-background p-2';
const DECIMAL = /^\d+(?:\.\d{1,4})?$/;

function normalizarDecimal(texto: string): string {
  return texto.trim().replace(',', '.');
}

/** Compara decimais não negativos em string, sem ponto flutuante: negativo, zero ou positivo. */
export function compararDecimais(a: string, b: string): number {
  const [inteiroA = '0', fracaoA = ''] = a.split('.');
  const [inteiroB = '0', fracaoB = ''] = b.split('.');
  const intA = inteiroA.replace(/^0+(?=\d)/, '');
  const intB = inteiroB.replace(/^0+(?=\d)/, '');
  if (intA.length !== intB.length) return intA.length - intB.length;
  if (intA !== intB) return intA < intB ? -1 : 1;
  const tamanho = Math.max(fracaoA.length, fracaoB.length);
  const fa = fracaoA.padEnd(tamanho, '0');
  const fb = fracaoB.padEnd(tamanho, '0');
  return fa === fb ? 0 : fa < fb ? -1 : 1;
}

/** Ajuda local: o servidor continua sendo a autoridade sobre saldo, destino e idempotência. */
export function validarQuantidadeEntrega(quantidade: string, pendente: string): string | null {
  const texto = normalizarDecimal(quantidade);
  if (!DECIMAL.test(texto)) return 'Informe uma quantidade válida, com até quatro casas decimais.';
  if (!/[1-9]/.test(texto)) return 'A quantidade deve ser maior que zero.';
  if (compararDecimais(texto, normalizarDecimal(pendente)) > 0) return 'A quantidade não pode ser maior que o saldo pendente.';
  return null;
}

export type ResultadoEntrega =
  | { readonly resultado: 'sucesso'; readonly chave: null }
  | { readonly resultado: 'conflito'; readonly chave: null; readonly mensagem: string }
  | { readonly resultado: 'incerto'; readonly chave: string; readonly mensagem: string }
  | { readonly resultado: 'recusado'; readonly chave: null; readonly mensagem: string };

export interface EntradaEntrega {
  readonly quantidade: string;
  readonly localizacaoOrigemId: string;
  readonly localizacaoDestinoId: string | null;
}

export interface EnviarEntregaParametros {
  readonly api: { readonly entregar: (id: string, input: ConfirmarEntregaAbastecimentoInput) => Promise<unknown> };
  readonly solicitacaoId: string;
  /** Item da linha clicada: obrigatório no servidor quando a solicitação tem mais de um material. */
  readonly solicitacaoItemId?: string | null;
  readonly entrada: EntradaEntrega;
  readonly chaveAtual: string | null;
  readonly gerarChave?: () => string;
}

/**
 * Códigos de negócio que o servidor devolve como 409 na entrega (o ledger recusa a saída sem saldo no local de
 * origem). São recusas definitivas para estes valores: a mensagem do servidor é mostrada e o diálogo continua
 * editável. Um 409 sem um destes códigos é conflito de concorrência e mantém o texto genérico com recarga.
 */
export const CODIGOS_RECUSA_NEGOCIO_ENTREGA: ReadonlySet<string> = new Set(['SALDO_INSUFICIENTE']);

function chaveNova(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

/**
 * Uma tentativa de entrega e o próximo estado da chave de idempotência (T-16-16-T): mantém a chave em
 * falha de rede/5xx (resultado incerto, a próxima tentativa reconcilia pela mesma chave) e a descarta em
 * sucesso ou recusa definitiva 4xx (inclusive 409 de negócio, como saldo insuficiente: a nova tentativa com
 * valores corrigidos usa chave nova). Nunca reenvia sozinho e nunca simula sucesso.
 */
export async function enviarEntrega({ api, solicitacaoId, solicitacaoItemId = null, entrada, chaveAtual, gerarChave = chaveNova }: EnviarEntregaParametros): Promise<ResultadoEntrega> {
  const tentativa = chaveAtual ?? gerarChave();
  try {
    await api.entregar(solicitacaoId, { quantidade: normalizarDecimal(entrada.quantidade), localizacaoOrigemId: entrada.localizacaoOrigemId.trim(), localizacaoDestinoId: entrada.localizacaoDestinoId?.trim() || null, chaveIdempotencia: tentativa, ...(solicitacaoItemId ? { solicitacaoItemId } : {}) });
    return { resultado: 'sucesso', chave: null };
  } catch (causa) {
    const status = causa instanceof ErroAbastecimento ? causa.status : null;
    if (status === null || status >= 500) return { resultado: 'incerto', chave: tentativa, mensagem: TEXTO_INCERTO };
    const codigo = causa instanceof ErroAbastecimento ? causa.codigo : null;
    if (status === 409 && (codigo === null || !CODIGOS_RECUSA_NEGOCIO_ENTREGA.has(codigo))) return { resultado: 'conflito', chave: null, mensagem: TEXTO_CONFLITO };
    return { resultado: 'recusado', chave: null, mensagem: causa instanceof Error ? causa.message : 'Não foi possível confirmar a entrega.' };
  }
}

export type SituacaoEnvioEntrega = 'ocioso' | 'enviando' | 'conflito' | 'incerto' | 'recusado';

export interface EntregaAbastecimentoCorpoProps {
  readonly linha: Pick<LinhaFila, 'modo' | 'material' | 'destino' | 'natureza' | 'unidade' | 'solicitadoTexto' | 'entregueTexto' | 'pendenteTexto'>;
  readonly quantidade: string;
  readonly origem: string;
  readonly destinoFisico: string;
  readonly erros?: { readonly quantidade?: string; readonly origem?: string; readonly destino?: string };
  readonly situacao: SituacaoEnvioEntrega;
  readonly mensagem?: string | null;
  readonly offline?: boolean;
  /** Em Radix o título precisa ser FormDialogTitle; no markup estático usa um h2 simples. */
  readonly titulo?: ReactNode;
  readonly descricao?: ReactNode;
  readonly onQuantidade?: (valor: string) => void;
  readonly onOrigem?: (valor: string) => void;
  readonly onDestinoFisico?: (valor: string) => void;
  readonly onConfirmar?: () => void;
  readonly onVoltar?: () => void;
  readonly onRecarregar?: () => void;
}

/** Corpo presentacional da confirmação de entrega física (UI-49, D-12). */
export function EntregaAbastecimentoCorpo({ linha, quantidade, origem, destinoFisico, erros = {}, situacao, mensagem, offline = false, titulo, descricao, onQuantidade, onOrigem, onDestinoFisico, onConfirmar, onVoltar, onRecarregar }: EntregaAbastecimentoCorpoProps): ReactElement {
  const enviando = situacao === 'enviando';
  const comAviso = situacao === 'conflito' || situacao === 'incerto';
  const exigeDestino = linha.modo === 'LOT_OP';
  return (
    <>
      <FormDialogHeader>
        {titulo ?? <h2 className="text-xl font-semibold">{TITULO_ENTREGA}</h2>}
        {descricao ?? <p className="text-sm text-muted-foreground">{CORPO_ENTREGA}</p>}
      </FormDialogHeader>
      <FormDialogBody className="space-y-4">
        <section className="space-y-1 rounded-md border border-border bg-muted p-4 text-sm">
          <p className="break-words font-semibold">{linha.material}</p>
          <p className="break-words">{linha.destino}</p>
          <p className="text-xs text-muted-foreground">{linha.natureza}</p>
          <p className="text-xs text-muted-foreground">A entrega física registra o movimento aplicável; separar não movimenta estoque.</p>
        </section>
        <label className="block text-sm font-semibold">Quantidade a entregar ({linha.unidade})
          <input inputMode="decimal" value={quantidade} onChange={(e) => onQuantidade?.(e.target.value)} className={`${CAMPO} text-right tabular-nums`} aria-invalid={erros.quantidade ? true : undefined} />
        </label>
        {erros.quantidade && <FormMessage>{erros.quantidade}</FormMessage>}
        <div className="block text-sm font-semibold">
          <label htmlFor="entrega-origem-fisica">Origem física</label>
          {/* WR-03: seleção de Localizacao pelo nome; o id segue internamente (nunca digitado nem exibido). */}
          <LocalizacaoSelect id="entrega-origem-fisica" rotulo="Origem física" value={origem} onChange={(id) => onOrigem?.(id)} className="mt-1 min-h-11 w-full" />
        </div>
        {erros.origem && <FormMessage>{erros.origem}</FormMessage>}
        {exigeDestino && (
          <>
            <div className="block text-sm font-semibold">
              <label htmlFor="entrega-destino-fisico">Destino físico (obrigatório)</label>
              <LocalizacaoSelect id="entrega-destino-fisico" rotulo="Destino físico (obrigatório)" value={destinoFisico} onChange={(id) => onDestinoFisico?.(id)} className="mt-1 min-h-11 w-full" />
            </div>
            {erros.destino && <FormMessage>{erros.destino}</FormMessage>}
          </>
        )}
        {(comAviso || situacao === 'recusado') && mensagem && (
          <div role="alert" className="space-y-2 rounded-md border border-danger p-4 text-sm text-danger">
            <p className="inline-flex items-start gap-2"><CircleAlert className="size-4 shrink-0" aria-hidden="true" /><span className="min-w-0 break-words">{mensagem}</span></p>
            {comAviso && <button type="button" className={BOTAO} onClick={onRecarregar}>Recarregar situação atual</button>}
          </div>
        )}
        {offline && <p role="status" className="text-sm text-warning">{TEXTO_OFFLINE}</p>}
      </FormDialogBody>
      <FormDialogFooter className="flex-col items-stretch gap-4">
        <dl className="grid grid-cols-3 gap-4 text-sm" data-slot="saldos-entrega">
          <div><dt className="text-xs font-semibold">Solicitado</dt><dd className="text-right tabular-nums">{linha.solicitadoTexto}</dd></div>
          <div><dt className="text-xs font-semibold">Já entregue</dt><dd className="text-right tabular-nums">{linha.entregueTexto}</dd></div>
          <div><dt className="text-xs font-semibold">Saldo pendente</dt><dd className="text-right tabular-nums font-semibold">{linha.pendenteTexto}</dd></div>
        </dl>
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className={BOTAO} onClick={onVoltar} disabled={enviando}>Voltar sem confirmar</button>
          <button type="button" className={BOTAO_PRIMARIO} onClick={onConfirmar} disabled={enviando || offline}>{enviando ? 'Confirmando…' : 'Confirmar entrega física'}</button>
        </div>
      </FormDialogFooter>
    </>
  );
}

export interface EntregaAbastecimentoDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly linha: LinhaFila;
  readonly offline?: boolean;
  readonly onEntregue: () => void;
  readonly onRecarregar: () => Promise<void>;
}

export function EntregaAbastecimentoDialog({ aberto, onAbertoChange, linha, offline = false, onEntregue, onRecarregar }: EntregaAbastecimentoDialogProps): ReactElement {
  const toast = useToast();
  const [quantidade, setQuantidade] = useState('');
  const [origem, setOrigem] = useState('');
  const [destinoFisico, setDestinoFisico] = useState('');
  const [erros, setErros] = useState<{ quantidade?: string; origem?: string; destino?: string }>({});
  const [situacao, setSituacao] = useState<SituacaoEnvioEntrega>('ocioso');
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [chave, setChave] = useState<string | null>(null);

  const confirmar = async (): Promise<void> => {
    if (situacao === 'enviando' || offline) return;
    const novosErros: { quantidade?: string; origem?: string; destino?: string } = {};
    const erroQuantidade = validarQuantidadeEntrega(quantidade, linha.pendente);
    if (erroQuantidade) novosErros.quantidade = erroQuantidade;
    if (!origem.trim()) novosErros.origem = 'Selecione a origem física.';
    if (linha.modo === 'LOT_OP' && !destinoFisico.trim()) novosErros.destino = 'Selecione o destino físico da transferência.';
    setErros(novosErros);
    if (Object.keys(novosErros).length > 0) return;
    setSituacao('enviando'); setMensagem(null);
    const resposta = await enviarEntrega({ api: abastecimentoApi, solicitacaoId: linha.id, solicitacaoItemId: linha.itemId, entrada: { quantidade, localizacaoOrigemId: origem, localizacaoDestinoId: linha.modo === 'LOT_OP' ? destinoFisico : null }, chaveAtual: chave });
    setChave(resposta.chave);
    if (resposta.resultado === 'sucesso') {
      toast.success({ title: 'Entrega física confirmada.' });
      onAbertoChange(false);
      onEntregue();
      return;
    }
    setSituacao(resposta.resultado);
    setMensagem(resposta.mensagem);
  };

  // Depois de uma recusa de negócio o diálogo segue editável; ao corrigir um valor a mensagem antiga sai.
  const editar = (aplicar: (valor: string) => void) => (valor: string): void => {
    aplicar(valor);
    if (situacao === 'recusado') { setSituacao('ocioso'); setMensagem(null); }
  };

  const recarregar = async (): Promise<void> => {
    await onRecarregar();
    setSituacao('ocioso'); setMensagem(null);
  };

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent>
        <EntregaAbastecimentoCorpo
          linha={linha} quantidade={quantidade} origem={origem} destinoFisico={destinoFisico} erros={erros} situacao={situacao} mensagem={mensagem} offline={offline}
          titulo={<FormDialogTitle className="text-xl font-semibold">{TITULO_ENTREGA}</FormDialogTitle>}
          descricao={<FormDialogDescription className="text-sm text-muted-foreground">{CORPO_ENTREGA}</FormDialogDescription>}
          onQuantidade={editar(setQuantidade)} onOrigem={editar(setOrigem)} onDestinoFisico={editar(setDestinoFisico)}
          onConfirmar={() => void confirmar()} onVoltar={() => onAbertoChange(false)} onRecarregar={() => void recarregar()}
        />
      </FormDialogContent>
    </FormDialog>
  );
}
